import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import { useDirtyTab } from "./settings/SettingsDirtyContext";
import {
    User as UserIcon, Mountain, Backpack, Users, Lock, Compass,
    Eye, Save, X, Plus, Globe, Crown,
} from "lucide-react";
import { toast } from "sonner";
import MarketplaceGearPicker from "../components/MarketplaceGearPicker";
import ImageUploader, { resolveImageUrl } from "../components/ImageUploader";
import InterestPicker from "../components/InterestPicker";
import { ADVENTURE_STYLES, STYLE_LABEL, interestLabel } from "../lib/interests";

// ---------- options ----------
const DIFFICULTIES = ["easy", "moderate", "hard", "expert"];
const TERRAIN = ["mountains", "forest", "desert", "lakes", "rivers", "coast", "plains", "snow"];
const SEASONS = ["spring", "summer", "fall", "winter"];
const TRIP_LENGTHS = ["half-day", "full-day", "weekend", "multi-day"];
const GROUP_TYPES = ["solo", "friends", "family", "small group", "guided"];
const EXP_LEVELS = ["new", "learning", "comfortable", "experienced", "expert"];
const CERTIFICATIONS = ["First Aid", "CPR", "Wilderness First Aid", "Firearm Safety", "Boater Safety"];
const REMOTE_LEVELS = [
    "not comfortable", "somewhat comfortable", "comfortable", "very comfortable", "expert",
];
const VEHICLES = ["2WD", "AWD", "4x4", "motorcycle", "boat", "RV", "none"];
const CAMPING_SETUP = ["tent", "hammock", "truck bed", "rooftop tent", "RV", "cabin"];
const NAV_TOOLS = ["phone maps", "GPS", "offline maps", "paper maps", "satellite messenger"];
const SAFETY_GEAR = ["first aid kit", "GPS", "satellite messenger", "radio", "bear spray", "emergency blanket", "fire starter"];
const VISIBILITY_OPTS = [
    { id: "public", label: "Public", desc: "Anyone can find you." },
    { id: "followers", label: "Followers only", desc: "Approved followers see full profile." },
    { id: "private", label: "Private", desc: "Locked from non-approved viewers." },
];
const SAMPLE_COVERS = [
    "https://images.unsplash.com/photo-1585094500201-1a2655fc8241?w=1600",
    "https://images.unsplash.com/photo-1762374008579-c883ef74267a?w=1600",
    "https://images.unsplash.com/photo-1680508128888-505d636574ff?w=1600",
    "https://images.unsplash.com/photo-1714577419068-45189e7bda58?w=1600",
    "https://images.unsplash.com/photo-1504851149312-7a075b496cc7?w=1600",
    "https://images.unsplash.com/photo-1571188654248-7a89213915f7?w=1600",
];

const blankForm = (u) => ({
    name: u?.name || "",
    username: u?.username || "",
    bio: u?.bio || "",
    location: u?.location || "",
    home_region: u?.home_region || "",
    website: u?.website || "",
    avatar: u?.avatar || "",
    cover: u?.cover || "",
    interests: u?.interests || [],
    ranked_interests: u?.ranked_interests || {},
    custom_interests: u?.custom_interests || [],
    adventure_styles: u?.adventure_styles || [],
    preferred_difficulty: u?.preferred_difficulty || [],
    preferred_terrain: u?.preferred_terrain || [],
    favorite_season: u?.favorite_season || [],
    typical_trip_length: u?.typical_trip_length || "",
    preferred_group_type: u?.preferred_group_type || [],
    experience_by_activity: u?.experience_by_activity || {},
    certifications: u?.certifications || [],
    years_experience: u?.years_experience || "",
    remote_comfort: u?.remote_comfort || "",
    favorite_gear: u?.favorite_gear || [],
    vehicle_access: u?.vehicle_access || "",
    camping_setup: u?.camping_setup || [],
    navigation_tools: u?.navigation_tools || [],
    safety_gear: u?.safety_gear || [],
    gear_wishlist: u?.gear_wishlist || [],
    marketplace_links: u?.marketplace_links || [],
    open_to_invites: u?.open_to_invites || false,
    looking_for_partners: u?.looking_for_partners || false,
    message_permissions: u?.message_permissions || "everyone",
    comment_permissions: u?.comment_permissions || "everyone",
    follow_request_behavior: u?.follow_request_behavior || "public",
    profile_visibility: u?.profile_visibility || "public",
    show_general_location_only: u?.show_general_location_only || false,
    hide_exact_locations_default: u?.hide_exact_locations_default || false,
    hide_gear_details: u?.hide_gear_details || false,
    allow_in_search: u?.allow_in_search ?? true,
    saved_visibility: u?.saved_visibility || "private",
});

export default function EditProfile({ embedded = false }) {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const initial = useMemo(() => blankForm(user), [user]);
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const patch = (updates) => setForm((f) => ({ ...f, ...updates }));

    useEffect(() => { if (user) setForm(blankForm(user)); }, [user]);

    const completion = useMemo(() => computeStrength(form), [form]);
    const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

    const submit = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
                username: form.username ? form.username.trim().toLowerCase() : null,
            };
            const { data } = await api.patch("/users/me", payload);
            updateUser(data);
            toast.success("Profile saved");
            if (!embedded) navigate("/profile");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useDirtyTab("profile", isDirty, submit);

    return (
        <div data-testid="edit-profile-page">
            {!embedded && <TopBar title="Build profile" />}
            <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10"}>
                {!embedded && (
                    <div className="mb-8">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">
                            Profile builder
                        </div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-midnight-pine tracking-tighter leading-[0.95]">
                            Build Your Adventure Profile
                        </h1>
                        <p className="text-slate-gray mt-3 max-w-2xl">
                            Show others what you love, what you're experienced in, and what kind of adventures you want to discover.
                        </p>
                    </div>
                )}

                <div className={embedded ? "grid lg:grid-cols-[1fr_320px] gap-6" : "grid lg:grid-cols-[1fr_360px] gap-8"}>
                    <form onSubmit={submit} className="space-y-6 min-w-0">
                        <Section icon={UserIcon} title="Public Profile" testid="section-public" hint="This is how other adventurers will see you across RealWorld Outdoors.">
                            <Field label="Display name" required>
                                <input data-testid="edit-name" required value={form.name} onChange={(e) => patch({ name: e.target.value })} className={inputCls} />
                            </Field>
                            <Field label="Username" hint="3–24 chars, lowercase letters / numbers / underscore.">
                                <div className="flex items-center gap-1">
                                    <span className="text-slate-gray font-bold">@</span>
                                    <input data-testid="edit-username" value={form.username} onChange={(e) => patch({ username: e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase() })} className={inputCls + " flex-1"} placeholder="sierrabennett" />
                                </div>
                            </Field>
                            <Field label="Bio">
                                <textarea data-testid="edit-bio" rows={3} value={form.bio} onChange={(e) => patch({ bio: e.target.value })} className={textareaCls} placeholder="Weekend hiker, backpacker, and mountain chaser." />
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="General location" hint="e.g. Boulder, CO">
                                    <input data-testid="edit-location" value={form.location} onChange={(e) => patch({ location: e.target.value })} className={inputCls} placeholder="Boulder, CO" />
                                </Field>
                                <Field label="Home region" hint="Used as a privacy-safe stand-in.">
                                    <input data-testid="edit-home-region" value={form.home_region} onChange={(e) => patch({ home_region: e.target.value })} className={inputCls} placeholder="Front Range, Colorado" />
                                </Field>
                            </div>
                            <Field label="Website / social link">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-slate-gray" />
                                    <input data-testid="edit-website" value={form.website} onChange={(e) => patch({ website: e.target.value })} className={inputCls + " flex-1"} placeholder="https://yoursite.com" />
                                </div>
                            </Field>
                            <Field label="Profile photo" hint="Upload a photo of yourself — JPG/PNG/WEBP up to 10MB.">
                                <ImageUploader
                                    testid="avatar"
                                    value={form.avatar}
                                    onChange={(v) => patch({ avatar: v })}
                                    aspect="aspect-square"
                                    fallback={initialsOf(form.name)}
                                />
                            </Field>
                            <Field label="Cover photo" hint="Upload your own or pick one from our gallery.">
                                <ImageUploader
                                    testid="cover"
                                    samples={SAMPLE_COVERS}
                                    value={form.cover}
                                    onChange={(v) => patch({ cover: v })}
                                    aspect="aspect-[16/9]"
                                />
                            </Field>
                        </Section>

                        <Section icon={Compass} title="Adventure Preferences" testid="section-prefs" hint="Tell RealWorld Outdoors what kind of adventures you care about most.">
                            <Field label="Activity interests" hint="Tap a category, pick what you enjoy, and mark how into each you are.">
                                <InterestPicker
                                    interests={form.interests}
                                    ranked={form.ranked_interests}
                                    custom={form.custom_interests}
                                    onChange={(next) => patch(next)}
                                />
                            </Field>
                            <Field label="Adventure style" hint="How you like to experience the outdoors — pick any that fit.">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {ADVENTURE_STYLES.map((s) => {
                                        const active = form.adventure_styles.includes(s.slug);
                                        return (
                                            <button
                                                key={s.slug}
                                                type="button"
                                                data-testid={`edit-style-${s.slug}`}
                                                onClick={() => patch({
                                                    adventure_styles: active
                                                        ? form.adventure_styles.filter((x) => x !== s.slug)
                                                        : [...form.adventure_styles, s.slug],
                                                })}
                                                className={`p-2.5 rounded-md text-left text-xs font-bold transition-all border ${
                                                    active ? "border-ember-orange bg-ember-orange/5 text-midnight-pine" : "border-ridge-line bg-snowcap text-granite hover:border-midnight-pine"
                                                }`}
                                            >{s.label}</button>
                                        );
                                    })}
                                </div>
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Preferred difficulty"><MultiChip options={DIFFICULTIES} value={form.preferred_difficulty} onChange={(v) => patch({ preferred_difficulty: v })} testid="diff" /></Field>
                                <Field label="Favorite season"><MultiChip options={SEASONS} value={form.favorite_season} onChange={(v) => patch({ favorite_season: v })} testid="season" /></Field>
                            </div>
                            <Field label="Preferred terrain"><MultiChip options={TERRAIN} value={form.preferred_terrain} onChange={(v) => patch({ preferred_terrain: v })} testid="terrain" /></Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Typical trip length"><ChipRow options={TRIP_LENGTHS} value={form.typical_trip_length} onChange={(v) => patch({ typical_trip_length: v })} testid="trip" clearable /></Field>
                                <Field label="Preferred group type"><MultiChip options={GROUP_TYPES} value={form.preferred_group_type} onChange={(v) => patch({ preferred_group_type: v })} testid="group" /></Field>
                            </div>
                        </Section>

                        <Section icon={Mountain} title="Experience" testid="section-experience" hint="Helps others understand what kinds of adventures fit you best.">
                            <Field label="Experience level by activity" hint="Optional — only the activities you select.">
                                <div className="space-y-2">
                                    {form.interests.length === 0 && (
                                        <p className="text-xs text-slate-gray italic">Pick activities above first.</p>
                                    )}
                                    {form.interests.map((act) => (
                                        <div key={act} className="flex items-center gap-3">
                                            <span className="text-sm font-bold text-midnight-pine w-32 sm:w-40">{interestLabel(act)}</span>
                                            <ChipRow options={EXP_LEVELS} value={form.experience_by_activity[act] || ""} onChange={(v) => patch({ experience_by_activity: { ...form.experience_by_activity, [act]: v } })} testid={`exp-${act}`} clearable />
                                        </div>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Certifications" hint="Optional. Shown publicly only if you choose.">
                                <MultiChip options={CERTIFICATIONS} value={form.certifications} onChange={(v) => patch({ certifications: v })} testid="cert" />
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Years of outdoor experience"><input data-testid="edit-years" type="number" min="0" value={form.years_experience} onChange={(e) => patch({ years_experience: e.target.value })} className={inputCls} placeholder="6" /></Field>
                                <Field label="Comfort with off-grid trips"><ChipRow options={REMOTE_LEVELS} value={form.remote_comfort} onChange={(v) => patch({ remote_comfort: v })} testid="remote" clearable /></Field>
                            </div>
                        </Section>

                        <Section icon={Backpack} title="My Outdoor Setup" testid="section-gear" hint="Share the gear, vehicles, and setup you use for your adventures.">
                            <Field label="Favorite gear"><TagInput value={form.favorite_gear} onChange={(v) => patch({ favorite_gear: v })} placeholder="Osprey pack, Garmin GPS, Jetboil" testid="fav-gear" /></Field>
                            <Field label="Vehicle / access type"><ChipRow options={VEHICLES} value={form.vehicle_access} onChange={(v) => patch({ vehicle_access: v })} testid="vehicle" clearable /></Field>
                            <Field label="Camping setup"><MultiChip options={CAMPING_SETUP} value={form.camping_setup} onChange={(v) => patch({ camping_setup: v })} testid="camping" /></Field>
                            <Field label="Navigation tools"><MultiChip options={NAV_TOOLS} value={form.navigation_tools} onChange={(v) => patch({ navigation_tools: v })} testid="nav" /></Field>
                            <Field label="Safety gear"><MultiChip options={SAFETY_GEAR} value={form.safety_gear} onChange={(v) => patch({ safety_gear: v })} testid="safety" /></Field>
                            <Field label="Gear wishlist"><TagInput value={form.gear_wishlist} onChange={(v) => patch({ gear_wishlist: v })} placeholder="Things you'd love to add" testid="wishlist" /></Field>
                            <Field label="Linked Marketplace gear" hint="Search RealWorld Outdoors Marketplace and attach listings to your gear setup.">
                                <MarketplaceGearPicker value={form.marketplace_links} onChange={(v) => patch({ marketplace_links: v })} />
                            </Field>
                            <div className="text-xs text-slate-gray italic px-3 py-2 rounded-md bg-trail-tan/30">
                                Tip: linked gear shows up on your public profile so others can buy what you actually use.
                            </div>
                        </Section>

                        <Section icon={Users} title="Community Settings" testid="section-community" hint="Control how other adventurers can connect with you.">
                            <ToggleRow icon={Users} title="Open to adventure invites" description="Show an Invite button on your public profile." checked={form.open_to_invites} onChange={(v) => patch({ open_to_invites: v })} testid="invites" />
                            <ToggleRow icon={Users} title="Looking for adventure partners" description="Display a 'Looking for partners' tag on your profile." checked={form.looking_for_partners} onChange={(v) => patch({ looking_for_partners: v })} testid="partners" />
                            <Field label="Message permissions">
                                <ChipRow options={[{ id: "everyone", label: "Everyone" }, { id: "followers", label: "Followers" }, { id: "none", label: "No one" }]} value={form.message_permissions} onChange={(v) => patch({ message_permissions: v })} testid="msg-perm" />
                            </Field>
                            <Field label="Comment permissions">
                                <ChipRow options={[{ id: "everyone", label: "Everyone" }, { id: "followers", label: "Followers" }, { id: "none", label: "No one" }]} value={form.comment_permissions} onChange={(v) => patch({ comment_permissions: v })} testid="cmt-perm" />
                            </Field>
                            <Field label="Follow request behavior">
                                <ChipRow options={[{ id: "public", label: "Public follow" }, { id: "approval", label: "Require approval" }, { id: "private", label: "Private profile" }]} value={form.follow_request_behavior} onChange={(v) => patch({ follow_request_behavior: v })} testid="follow-behavior" />
                            </Field>
                        </Section>

                        <Section icon={Lock} title="Privacy & Location Safety" testid="section-privacy" hint="Protect sensitive outdoor locations and control what others can see.">
                            <Field label="Profile visibility">
                                <div className="grid sm:grid-cols-3 gap-2">
                                    {VISIBILITY_OPTS.map((v) => (
                                        <button
                                            key={v.id}
                                            type="button"
                                            data-testid={`visibility-${v.id}`}
                                            onClick={() => patch({ profile_visibility: v.id })}
                                            className={`p-3 rounded-md border text-left transition-all ${
                                                form.profile_visibility === v.id
                                                    ? "border-ember-orange bg-ember-orange/5"
                                                    : "border-ridge-line bg-snowcap hover:border-midnight-pine"
                                            }`}
                                        >
                                            <div className="text-sm font-bold text-midnight-pine">{v.label}</div>
                                            <div className="text-[11px] text-slate-gray leading-tight mt-0.5">{v.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <ToggleRow testid="general-only" icon={Globe} title="Show general location only" description="Only your home region shows publicly." checked={form.show_general_location_only} onChange={(v) => patch({ show_general_location_only: v })} />
                            <ToggleRow testid="hide-exact-default" icon={Lock} title="Hide exact adventure locations by default" description="New adventures will start with their location fuzzed for non-followers." checked={form.hide_exact_locations_default} onChange={(v) => patch({ hide_exact_locations_default: v })} />
                            <ToggleRow testid="hide-gear" icon={Backpack} title="Hide gear details" description="Outdoor Setup section will be hidden from public viewers." checked={form.hide_gear_details} onChange={(v) => patch({ hide_gear_details: v })} />
                            <ToggleRow testid="in-search" icon={Eye} title="Allow profile in search" description="When off, your profile won't appear in search/discovery results." checked={form.allow_in_search} onChange={(v) => patch({ allow_in_search: v })} />
                            <Field label="Saved items visibility">
                                <ChipRow options={[{ id: "private", label: "Private" }, { id: "followers", label: "Followers only" }, { id: "public", label: "Public" }]} value={form.saved_visibility} onChange={(v) => patch({ saved_visibility: v })} testid="saved-vis" />
                            </Field>
                        </Section>

                        <div data-testid="edit-action-bar" className="sticky bottom-16 lg:bottom-4 z-10 bg-snowcap border border-ridge-line rounded-lg p-3 shadow-lg flex flex-wrap items-center gap-2">
                            <button type="button" data-testid="edit-cancel" onClick={() => navigate(-1)} className="px-4 py-2.5 rounded-md border border-ridge-line text-granite font-bold text-sm hover:bg-alpine-mist transition-all">
                                <X className="w-4 h-4 inline mr-1" /> Cancel
                            </button>
                            <button type="button" data-testid="edit-preview" onClick={() => navigate("/profile")} className="px-4 py-2.5 rounded-md border border-ridge-line text-granite font-bold text-sm hover:bg-alpine-mist transition-all">
                                <Eye className="w-4 h-4 inline mr-1" /> Preview profile
                            </button>
                            <div className="flex-1" />
                            <button type="submit" data-testid="edit-save" disabled={loading} className="px-6 py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all inline-flex items-center gap-2">
                                <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save Adventure Profile"}
                            </button>
                        </div>
                    </form>

                    <aside className="hidden lg:block">
                        <div className="sticky top-6 space-y-4">
                            <StrengthPanel completion={completion} />
                            <ProfilePreview form={form} user={user} />
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

// ---------- helpers ----------
const inputCls = "w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 transition-colors text-granite";
const textareaCls = inputCls + " resize-none";

function initialsOf(name) {
    return (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
}

function computeStrength(f) {
    const checks = [
        { key: "avatar", label: "Profile photo added", done: !!f.avatar },
        { key: "cover", label: "Cover photo added", done: !!f.cover },
        { key: "bio", label: "Bio completed", done: !!f.bio.trim() },
        { key: "username", label: "Username set", done: !!f.username },
        { key: "interests", label: "Interests selected", done: f.interests.length > 0 },
        { key: "style", label: "Adventure style chosen", done: f.adventure_styles.length > 0 },
        { key: "experience", label: "Experience levels added", done: Object.keys(f.experience_by_activity).length > 0 },
        { key: "gear", label: "Gear setup added", done: f.favorite_gear.length > 0 || !!f.vehicle_access },
        { key: "privacy", label: "Privacy reviewed", done: !!f.profile_visibility },
    ];
    return { checks, percent: Math.round((checks.filter((c) => c.done).length / checks.length) * 100) };
}

// ---------- subcomponents ----------
function Section({ icon: Icon, title, hint, testid, children }) {
    return (
        <section data-testid={testid} className="bg-snowcap border border-ridge-line rounded-xl p-5 sm:p-6 space-y-5">
            <header className="pb-3 border-b border-ridge-line">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="font-display font-black text-xl text-midnight-pine tracking-tight">{title}</h2>
                </div>
                {hint && <p className="text-xs text-slate-gray mt-2 ml-12">{hint}</p>}
            </header>
            {children}
        </section>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1.5 block">
                {label} {required && <span className="text-ember-orange">*</span>}
            </label>
            {hint && <p className="text-xs text-slate-gray mb-2">{hint}</p>}
            {children}
        </div>
    );
}

function ChipRow({ options, value, onChange, testid, clearable = false }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const id = typeof opt === "string" ? opt : opt.id;
                const label = typeof opt === "string" ? opt : opt.label;
                const active = value === id;
                return (
                    <button key={id} type="button" data-testid={`${testid}-${id}`} onClick={() => onChange(active && clearable ? "" : id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all capitalize ${
                            active ? "bg-ember-orange text-white" : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
                        }`}>{label}</button>
                );
            })}
        </div>
    );
}

function MultiChip({ options, value, onChange, testid }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const active = value.includes(opt);
                return (
                    <button key={opt} type="button" data-testid={`${testid}-${opt}`}
                        onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...value, opt])}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all capitalize ${
                            active ? "bg-midnight-pine text-white" : "bg-snowcap text-granite border border-ridge-line hover:border-midnight-pine"
                        }`}>{opt}</button>
                );
            })}
        </div>
    );
}

function ToggleRow({ icon: Icon, title, description, checked, onChange, testid }) {
    return (
        <div data-testid={`togglerow-${testid}`} className="flex items-start gap-3 p-4 rounded-md border border-ridge-line bg-alpine-mist/40">
            {Icon && <div className="w-9 h-9 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>}
            <div className="flex-1 min-w-0">
                <div className="font-bold text-granite text-sm">{title}</div>
                {description && <p className="text-xs text-slate-gray mt-0.5">{description}</p>}
            </div>
            <button type="button" role="switch" aria-checked={checked} data-testid={`toggle-${testid}`} onClick={() => onChange(!checked)}
                className={`relative w-11 h-6 shrink-0 rounded-full transition-colors mt-0.5 ${checked ? "bg-ember-orange" : "bg-ridge-line"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-snowcap shadow-sm transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
            </button>
        </div>
    );
}

function TagInput({ value, onChange, placeholder, testid }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const t = draft.trim();
        if (!t) return;
        if (!value.includes(t)) onChange([...value, t]);
        setDraft("");
    };
    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <input data-testid={`taginput-${testid}`} value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
                    placeholder={placeholder} className={inputCls + " flex-1"} />
                <button type="button" data-testid={`taginput-add-${testid}`} onClick={add} className="px-4 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen transition-all">
                    <Plus className="w-4 h-4" />
                </button>
            </div>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {value.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-trail-tan/60 text-midnight-pine text-xs font-bold">
                            {t}
                            <button type="button" data-testid={`taginput-remove-${testid}-${t}`} onClick={() => onChange(value.filter((x) => x !== t))} className="w-4 h-4 rounded-full hover:bg-midnight-pine/10 flex items-center justify-center">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function StrengthPanel({ completion }) {
    return (
        <div data-testid="profile-strength-panel" className="bg-snowcap border border-ridge-line rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-black text-base text-midnight-pine">Profile Strength</h3>
                <span className="font-display font-black text-2xl text-ember-orange">{completion.percent}%</span>
            </div>
            <div className="h-1.5 bg-ridge-line rounded-full overflow-hidden mb-4">
                <div className="h-full bg-ember-orange transition-all duration-500" style={{ width: `${completion.percent}%` }} />
            </div>
            <ul className="space-y-1.5">
                {completion.checks.map((c) => (
                    <li key={c.key} data-testid={`strength-${c.key}`} className="flex items-center gap-2 text-xs">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${c.done ? "bg-moss text-white" : "bg-trail-tan text-slate-gray"}`}>
                            {c.done ? "✓" : "•"}
                        </span>
                        <span className={c.done ? "text-granite" : "text-slate-gray"}>{c.label}</span>
                    </li>
                ))}
            </ul>
            {completion.percent < 100 && (
                <p className="text-xs text-ember-orange font-bold mt-4">Complete Your Adventure Profile</p>
            )}
        </div>
    );
}

function ProfilePreview({ form, user }) {
    return (
        <div data-testid="profile-preview-card">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-gray mb-2 flex items-center gap-2">
                <Eye className="w-3 h-3" /> Live preview
            </div>
            <div className="bg-snowcap border border-ridge-line rounded-xl overflow-hidden">
                <div className="h-24 bg-midnight-pine relative overflow-hidden">
                    {form.cover && <img src={resolveImageUrl(form.cover)} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
                <div className="p-4 -mt-8 relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-trail-tan border-4 border-snowcap shadow-md flex items-center justify-center text-midnight-pine font-display font-black text-xl mb-3">
                        {form.avatar ? <img src={resolveImageUrl(form.avatar)} alt="" className="w-full h-full object-cover" /> : initialsOf(form.name)}
                    </div>
                    <div className="flex items-center gap-2">
                        <h4 className="font-display font-black text-lg text-midnight-pine">{form.name || "Your name"}</h4>
                        {user?.tier === "premium" && <Crown className="w-4 h-4 text-ember-orange" />}
                    </div>
                    {form.username && <div className="text-xs text-slate-gray font-semibold">@{form.username}</div>}
                    {form.location && <div className="text-xs text-slate-gray mt-1">📍 {form.location}</div>}
                    {form.bio && <p className="text-sm text-granite mt-2 line-clamp-2">{form.bio}</p>}
                    {form.adventure_styles.length > 0 && (
                        <span className="inline-block mt-2 px-2.5 py-1 rounded-full bg-midnight-pine text-white text-[10px] font-bold uppercase tracking-widest">
                            {STYLE_LABEL[form.adventure_styles[0]] || form.adventure_styles[0]}
                            {form.adventure_styles.length > 1 ? ` +${form.adventure_styles.length - 1}` : ""}
                        </span>
                    )}
                    {form.interests.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                            {form.interests.slice(0, 4).map((i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full bg-trail-tan/60 text-midnight-pine text-[10px] font-bold">{interestLabel(i)}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
